import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import HttpManager from "../../../services/HttpManager";
import { Sticker } from "../../../services/v2/prospects.service";
import WappIcono from "../../../components/wappIcono";
import { errorTexto } from "./salesHelpers";

/**
 * Stickers de WappTeam y WappChat (Esteban, 2026-10-14: "no hay capacidad de hacer stickers. Esto urge").
 *
 * - "Mis Stickers": los que cada quien hizo con una foto y los que guardó de un mensaje (de una compañera o de un cliente).
 * - Crear: se elige una foto, se acomoda (arrastrar y zoom), forma (cuadro redondeado, círculo o completa) y un texto
 *   opcional. Todo se dibuja AQUÍ en el navegador a 512×512; al servidor solo llega el PNG final y lo deja en WEBP.
 * - Cuidar el servidor (Esteban: "que no vayamos a causar un colapso de sobrecarga"): la lista se pide una vez por
 *   sesión (y se rehace sola cada 6 h, porque las URLs firmadas duran 48 h); cada imagen tiene URL fija todo el día,
 *   así el navegador la baja una vez y la reusa en la lista y en todos los mensajes.
 */

// ------------------------------------------------------------------ lista en memoria (una por sesión)

const VIGENCIA = 6 * 3600 * 1000;
let cache: { campusId: number; at: number; p: Promise<Sticker[]> } | null = null;
const oyentes = new Set<(l: Sticker[]) => void>();
const avisar = () => { if (cache) cache.p.then((l) => oyentes.forEach((f) => f(l))).catch(() => null); };

export const misStickers = (campusId: number): Promise<Sticker[]> => {
	if (!cache || Date.now() - cache.at > VIGENCIA) {
		const p = HttpManager.getInstance().prospectsService.stickers(campusId).then((r) => r.data.stickers || []);
		cache = { campusId, at: Date.now(), p };
		p.catch(() => { if (cache && cache.p === p) cache = null; });
	}
	return cache.p;
};
const ponerEnLista = (s: Sticker) => {
	if (!cache) return;
	cache = { ...cache, p: cache.p.then((l) => [s, ...l.filter((x) => x.id !== s.id)]) };
	avisar();
};
const quitarDeLista = (id: number) => {
	if (!cache) return;
	cache = { ...cache, p: cache.p.then((l) => l.filter((x) => x.id !== id)) };
	avisar();
};

/** Aviso cortito abajo al centro (2.5 s). Sirve igual en WappTeam, su ventanita y WappChat. */
const avisoCorto = (texto: string) => {
	const el = document.createElement("div");
	el.className = "stk-aviso";
	el.setAttribute("role", "status");
	el.textContent = texto;
	document.body.appendChild(el);
	setTimeout(() => el.remove(), 2600);
};

/** Guardar en Mis Stickers el sticker de un mensaje (de WappTeam o de WhatsApp) y avisar cómo salió. */
export const guardarSticker = async (pedir: () => Promise<{ data: Sticker }>): Promise<void> => {
	try {
		const r = await pedir();
		ponerEnLista(r.data);
		avisoCorto(r.data.yaEstaba ? "Ese sticker ya estaba en tus stickers" : "Guardado en tus stickers");
	} catch (e) {
		avisoCorto(errorTexto(e));
	}
};

// ------------------------------------------------------------------ piezas

/** Ícono de sticker (hoja con la esquina doblada), del mismo trazo que WappIcono. */
export const IconoSticker: React.FC = () => (
	<svg className="stk-ico" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		<path d="M20 12l-2 .5a6 6 0 0 1-6.5-6.5l.5-2" /><path d="M20 12a8 8 0 1 1-8-8l8 8" />
	</svg>
);

/** El sticker dentro de un mensaje: sin burbuja, como WhatsApp. Si la imagen ya no carga, lo dice. */
export const StickerImg: React.FC<{ url: string | null | undefined; chico?: boolean }> = ({ url, chico }) => {
	const [mal, setMal] = useState(false);
	if (!url) return <span className={"stk-msg cargando" + (chico ? " chico" : "")} />;
	if (mal) return <span className="stk-msg mal">Sticker no disponible</span>;
	return <img className={"stk-msg" + (chico ? " chico" : "")} src={url} alt="Sticker" loading="lazy" draggable={false} onError={() => setMal(true)} />;
};

/** Pestaña Stickers del panel de emojis: Crear + Mis Stickers. Tocar uno lo manda. */
export const PanelStickers: React.FC<{ campusId: number; onElegir: (s: Sticker) => Promise<void>; onCrear: () => void }> = ({ campusId, onElegir, onCrear }) => {
	const [lista, setLista] = useState<Sticker[] | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [editando, setEditando] = useState(false);
	const [enviando, setEnviando] = useState<number | null>(null);
	useEffect(() => {
		let vivo = true;
		const f = (l: Sticker[]) => { if (vivo) setLista(l); };
		oyentes.add(f);
		misStickers(campusId).then(f).catch((e) => { if (vivo) setError(errorTexto(e)); });
		return () => { vivo = false; oyentes.delete(f); };
	}, [campusId]);
	// Sin stickers ya no hay nada que quitar: vuelve el botón Crear.
	useEffect(() => { if (lista && !lista.length) setEditando(false); }, [lista]);
	const elegir = async (s: Sticker) => {
		if (enviando) return;
		if (editando) {
			try { await HttpManager.getInstance().prospectsService.stickerBorrar(campusId, s.id); quitarDeLista(s.id); } catch (e) { setError(errorTexto(e)); }
			return;
		}
		setEnviando(s.id);
		setError(null);
		try { await onElegir(s); } catch (e) { setError(errorTexto(e)); } finally { setEnviando(null); }
	};
	return (
		<>
			<div className="eqg-titulo stk-titulo">
				<span>Mis Stickers</span>
				{lista && lista.length ? <button type="button" className="stk-editar" onMouseDown={(e) => e.preventDefault()} onClick={() => setEditando(!editando)}>{editando ? "Listo" : "Quitar"}</button> : null}
			</div>
			{error ? <div className="stk-error" role="alert">{error}</div> : null}
			<div className={"stk-rejilla" + (editando ? " editando" : "")}>
				{!editando ? (
					<button type="button" className="stk-crear" onMouseDown={(e) => e.preventDefault()} onClick={onCrear} aria-label="Crear Sticker">
						<WappIcono n="plus" /><span>Crear</span>
					</button>
				) : null}
				{lista === null && !error ? [0, 1, 2].map((i) => <span key={i} className="stk-item cargando" />) : null}
				{(lista || []).map((s) => (
					<button key={s.id} type="button" className={"stk-item" + (enviando === s.id ? " enviando" : "")} onMouseDown={(e) => e.preventDefault()} onClick={() => elegir(s)}
						aria-label={editando ? "Quitar de mis stickers" : "Mandar sticker"} title={editando ? "Quitar de mis stickers" : "Mandar"} disabled={!!enviando && enviando !== s.id}>
						{s.url ? <img src={s.url} alt="" loading="lazy" draggable={false} /> : null}
						{editando ? <span className="stk-quitar" aria-hidden="true"><WappIcono n="x" /></span> : null}
					</button>
				))}
				{lista && !lista.length ? <div className="stk-vacio">Crea uno con una foto o guarda los que te manden.</div> : null}
			</div>
		</>
	);
};

// ------------------------------------------------------------------ crear un sticker

type Forma = "cuadro" | "circulo" | "completa";
const TAM = 512;
const COLORES = [{ n: "Blanco", c: "#ffffff", borde: "#1b1b1b" }, { n: "Amarillo", c: "#FFD43B", borde: "#1b1b1b" }, { n: "Negro", c: "#1b1b1b", borde: "#ffffff" }];

const redondeado = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
};

/** Dibuja el sticker a 512×512 (lo mismo para la vista previa y para el archivo final). */
const dibujar = (ctx: CanvasRenderingContext2D, img: HTMLImageElement, forma: Forma, zoom: number, dx: number, dy: number, texto: string, color: number) => {
	ctx.clearRect(0, 0, TAM, TAM);
	ctx.save();
	if (forma === "circulo") { ctx.beginPath(); ctx.arc(TAM / 2, TAM / 2, TAM / 2 - 6, 0, Math.PI * 2); ctx.clip(); }
	else if (forma === "cuadro") { redondeado(ctx, 6, 6, TAM - 12, TAM - 12, 64); ctx.clip(); }
	const w = img.naturalWidth, h = img.naturalHeight;
	const base = forma === "completa" ? Math.min(TAM / w, TAM / h) : Math.max(TAM / w, TAM / h);
	const k = base * zoom;
	ctx.imageSmoothingQuality = "high";
	ctx.drawImage(img, TAM / 2 + dx - (w * k) / 2, TAM / 2 + dy - (h * k) / 2, w * k, h * k);
	ctx.restore();
	const t = texto.trim();
	if (t) {
		const col = COLORES[color] || COLORES[0];
		let size = 76;
		const fuente = (s: number) => "800 " + s + "px Bricolage, Hanken, system-ui, sans-serif";
		ctx.font = fuente(size);
		while (size > 30 && ctx.measureText(t).width > TAM - 56) { size -= 4; ctx.font = fuente(size); }
		ctx.textAlign = "center";
		ctx.textBaseline = "alphabetic";
		ctx.lineJoin = "round";
		ctx.lineWidth = Math.max(8, size / 5);
		ctx.strokeStyle = col.borde;
		ctx.fillStyle = col.c;
		const y = TAM - 44;
		ctx.strokeText(t, TAM / 2, y, TAM - 40);
		ctx.fillText(t, TAM / 2, y, TAM - 40);
	}
};

export const CrearSticker: React.FC<{ campusId: number; onCerrar: () => void; onCreado: (s: Sticker) => void }> = ({ campusId, onCerrar, onCreado }) => {
	const [img, setImg] = useState<HTMLImageElement | null>(null);
	const [forma, setForma] = useState<Forma>("cuadro");
	const [zoom, setZoom] = useState(1);
	const [pos, setPos] = useState({ dx: 0, dy: 0 });
	const [texto, setTexto] = useState("");
	const [color, setColor] = useState(0);
	const [guardando, setGuardando] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const lienzo = useRef<HTMLCanvasElement>(null);
	const archivo = useRef<HTMLInputElement>(null);
	const arrastre = useRef<{ x: number; y: number; dx: number; dy: number; id: number } | null>(null);
	const urlObj = useRef<string | null>(null);

	const cargar = useCallback((f: File | Blob | null | undefined) => {
		if (!f) return;
		if (f.type && !f.type.startsWith("image/")) { setError("Elige una foto o imagen."); return; }
		setError(null);
		const u = URL.createObjectURL(f);
		const i = new Image();
		i.onload = () => {
			if (urlObj.current) URL.revokeObjectURL(urlObj.current);
			urlObj.current = u;
			setImg(i); setZoom(1); setPos({ dx: 0, dy: 0 });
		};
		i.onerror = () => { URL.revokeObjectURL(u); setError("Esa imagen no se puede abrir aquí. Prueba con una JPG o PNG."); };
		i.src = u;
	}, []);

	// Pegar una imagen (Ctrl+V / Cmd+V) mientras está abierto; Escape cierra.
	useEffect(() => {
		const pegar = (e: ClipboardEvent) => {
			const it = Array.from(e.clipboardData?.items || []).find((x) => x.type.startsWith("image/"));
			if (it) { e.preventDefault(); cargar(it.getAsFile()); }
		};
		const esc = (e: KeyboardEvent) => { if (e.key === "Escape" && !guardando) onCerrar(); };
		document.addEventListener("paste", pegar);
		document.addEventListener("keydown", esc);
		return () => { document.removeEventListener("paste", pegar); document.removeEventListener("keydown", esc); };
	}, [cargar, onCerrar, guardando]);
	useEffect(() => () => { if (urlObj.current) URL.revokeObjectURL(urlObj.current); }, []);

	// La fuente del texto puede no estar lista la primera vez: se vuelve a dibujar cuando cargue.
	const [, setFuenteLista] = useState(0);
	useEffect(() => {
		const f = (document as any).fonts;
		if (f?.load) f.load("800 40px Bricolage").then(() => setFuenteLista((n) => n + 1)).catch(() => null);
	}, []);
	useEffect(() => {
		const c = lienzo.current;
		const ctx = c?.getContext("2d");
		if (c && ctx && img) dibujar(ctx, img, forma, zoom, pos.dx, pos.dy, texto, color);
	});

	const escalaPantalla = () => { const c = lienzo.current; return c ? TAM / c.getBoundingClientRect().width : 1; };
	const alBajar = (e: React.PointerEvent<HTMLCanvasElement>) => {
		if (!img) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		arrastre.current = { x: e.clientX, y: e.clientY, dx: pos.dx, dy: pos.dy, id: e.pointerId };
	};
	const alMover = (e: React.PointerEvent<HTMLCanvasElement>) => {
		const a = arrastre.current;
		if (!a || a.id !== e.pointerId) return;
		const k = escalaPantalla();
		setPos({ dx: a.dx + (e.clientX - a.x) * k, dy: a.dy + (e.clientY - a.y) * k });
	};
	const alSoltar = () => { arrastre.current = null; };
	const alRueda = (e: React.WheelEvent<HTMLCanvasElement>) => {
		if (!img) return;
		setZoom((z) => Math.min(4, Math.max(0.3, z * (e.deltaY < 0 ? 1.08 : 1 / 1.08))));
	};

	const guardar = async () => {
		if (!img || guardando) return;
		setGuardando(true);
		setError(null);
		try {
			const c = document.createElement("canvas");
			c.width = TAM; c.height = TAM;
			const ctx = c.getContext("2d");
			if (!ctx) throw new Error("Tu navegador no pudo preparar el sticker.");
			dibujar(ctx, img, forma, zoom, pos.dx, pos.dy, texto, color);
			const blob = await new Promise<Blob | null>((ok) => c.toBlob(ok, "image/png"));
			if (!blob) throw new Error("Tu navegador no pudo preparar el sticker.");
			const r = await HttpManager.getInstance().prospectsService.stickerCrear(campusId, blob);
			ponerEnLista(r.data);
			onCreado(r.data);
		} catch (e) {
			setError(errorTexto(e));
			setGuardando(false);
		}
	};

	// En document.body: dentro del chat, WappTeam atrapa la foto soltada o pegada como archivo adjunto y cierra este cuadro.
	return createPortal(
		<div className="stk-velo" onMouseDown={(e) => { if (e.target === e.currentTarget && !guardando) onCerrar(); }}>
			<div className="stk-dlg" role="dialog" aria-label="Crear Sticker" onDragOver={(e) => e.preventDefault()}
				onDrop={(e) => { e.preventDefault(); cargar(e.dataTransfer.files?.[0]); }}>
				<div className="stk-cab">
					<b>Crear Sticker</b>
					<button type="button" className="stk-x" onClick={onCerrar} disabled={guardando} aria-label="Cerrar"><WappIcono n="x" /></button>
				</div>
				<input ref={archivo} type="file" accept="image/*" hidden onChange={(e) => { cargar(e.target.files?.[0]); e.target.value = ""; }} />
				{!img ? (
					<button type="button" className="stk-elegir" onClick={() => archivo.current?.click()}>
						<WappIcono n="photo" />
						<b>Elegir Foto</b>
						<span>o arrástrala aquí, o pégala con Ctrl+V</span>
					</button>
				) : (
					<>
						<div className="stk-lienzo">
							<canvas ref={lienzo} width={TAM} height={TAM} onPointerDown={alBajar} onPointerMove={alMover} onPointerUp={alSoltar} onPointerCancel={alSoltar}
								onWheel={alRueda} aria-label="Vista previa: arrastra para acomodar" />
						</div>
						<div className="stk-nota">Arrastra la foto para acomodarla.</div>
						<div className="stk-fila">
							<span className="stk-et">Tamaño</span>
							<input type="range" min={0.3} max={4} step={0.01} value={zoom} onChange={(e) => setZoom(+e.target.value)} aria-label="Tamaño de la foto" />
						</div>
						<div className="stk-fila">
							<span className="stk-et">Forma</span>
							<div className="stk-opciones" role="radiogroup" aria-label="Forma">
								{([["cuadro", "Cuadro"], ["circulo", "Círculo"], ["completa", "Completa"]] as [Forma, string][]).map(([f, n]) => (
									<button key={f} type="button" role="radio" aria-checked={forma === f} className={forma === f ? "activo" : ""} onClick={() => { setForma(f); setZoom(1); setPos({ dx: 0, dy: 0 }); }}>{n}</button>
								))}
							</div>
						</div>
						<div className="stk-fila">
							<span className="stk-et">Texto</span>
							<input className="stk-texto" type="text" value={texto} maxLength={32} onChange={(e) => setTexto(e.target.value)} placeholder="Opcional" aria-label="Texto del sticker" />
							<div className="stk-colores" role="radiogroup" aria-label="Color del texto">
								{COLORES.map((c, i) => <button key={c.n} type="button" role="radio" aria-checked={color === i} aria-label={c.n} title={c.n}
									className={color === i ? "activo" : ""} style={{ background: c.c }} onClick={() => setColor(i)} />)}
							</div>
						</div>
					</>
				)}
				{error ? <div className="stk-error" role="alert">{error}</div> : null}
				<div className="stk-pie">
					{img ? <button type="button" className="stk-boton" onClick={() => archivo.current?.click()} disabled={guardando}>Cambiar Foto</button> : <span />}
					<button type="button" className="stk-boton pri" onClick={guardar} disabled={!img || guardando}>{guardando ? "Guardando…" : "Guardar Sticker"}</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};
